import type { CerteficateTab, Sort } from 'types'

export const PAGE_SIZE = 10

export const ALLOWED_TABS: CerteficateTab[] = [
  'all',
  'active',
  'transfer',
  'stolen',
  'cancel request',
  'lost',
]

export const DEFAULT_FILTERS = {
  limit: PAGE_SIZE,
  offset: 0,
  tab: 'all' as CerteficateTab,
  status: null,
  type: null,
  sort: null,
  sort_by: null,
  query: null,
  certificateIds: null,
  activityFrom: undefined,
  activityTo: undefined,
  statuses: [] as string[],
  // VRG-511 — the list has always meant "what I hold now", and that stays the
  // default. Past-owned items are opt-in via the filter below; a filter that
  // changed what a list contains without being asked would be a surprise, not a
  // feature.
  ownership: 'current' as OwnershipFilterValue,
}

/**
 * VRG-511 — the ownership scope of the certificates list.
 *
 * Mirrors the API's `CertificateOwnershipScope` exactly, and the values are sent
 * verbatim as the `ownership` query param — this is a SERVER-side filter, unlike
 * `statuses` below, because a past-owned certificate is not in the client's array
 * to be filtered out of. There is deliberately no client-side predicate for it.
 */
export type OwnershipFilterValue = 'current' | 'past' | 'all'

/**
 * Rendered by the same `StatusPillFilter` the status row uses (§28/§47 — reuse the
 * control the drawer already has rather than introducing a second idiom for
 * "pick one of these").
 *
 * Single-select, unlike the status pills, which is enforced by the page's toggle
 * handler rather than by the component: the three values are mutually exclusive
 * scopes, not a set. Neutral grey throughout, deliberately — the status pills
 * borrow their fill from the StatusBadge on the cards so filter and card read as
 * the same thing, and ownership is not a status, so borrowing a status hue here
 * would assert a relationship that does not exist.
 */
export const OWNERSHIP_FILTER_OPTIONS: {
  value: OwnershipFilterValue
  label: string
  fillColor: string
  borderColor: string
  textColor: string
}[] = [
  { value: 'current', label: 'Owned', fillColor: '#F3F4F6', borderColor: '#E5E7EB', textColor: '#374151' },
  { value: 'past', label: 'Previously owned', fillColor: '#F3F4F6', borderColor: '#E5E7EB', textColor: '#374151' },
  { value: 'all', label: 'All', fillColor: '#F3F4F6', borderColor: '#E5E7EB', textColor: '#374151' },
]

// VERA-315: Status filter families — the multi-select pill filter (StatusPillFilter,
// platform StatusChipFilter parity) toggles these family keys, not raw certificate.status
// values. A selected pill renders as a FILLED chip identical to the StatusBadge shown on
// the cards: fill/border/text come straight from displayStatusColorMapper so the filter and
// the badge read as the same status. Families with no direct mapper entry (all / transfer /
// cancel request) use the nearest palette hue (neutral / buy_offer blue / amber).
export type StatusFilterOption = {
  value: string
  label: string
  /** Solid chip fill when selected (StatusBadge fill). */
  fillColor: string
  /** Chip border when selected (StatusBadge border). */
  borderColor: string
  /** Chip text when selected (StatusBadge text). */
  textColor: string
}

export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: 'all', label: 'All', fillColor: '#F3F4F6', borderColor: '#E5E7EB', textColor: '#374151' },
  { value: 'active', label: 'Active', fillColor: '#DCFCE8', borderColor: '#BBF7D1', textColor: '#14532B' },
  { value: 'transfer', label: 'Transfer', fillColor: '#DBE8FE', borderColor: '#BFD7FE', textColor: '#1E478A' },
  { value: 'stolen', label: 'Stolen', fillColor: '#FEE2E2', borderColor: '#FECACA', textColor: '#7F1D1D' },
  { value: 'cancel request', label: 'Cancel Request', fillColor: '#FEF3C7', borderColor: '#FDE68A', textColor: '#92400E' },
  { value: 'lost', label: 'Lost', fillColor: '#FFE6D5', borderColor: '#FECCAA', textColor: '#7C3D12' },
]

// Family -> underlying raw certificate.status values it covers. The 'transfer' family
// list is also mirrored in utils/certificateStatusLabel.ts (status -> label),
// not imported, to avoid a circular import between this constants file and that hook).
export const STATUS_FAMILY_MAP: Record<string, string[]> = {
  active: ['active'],
  transfer: [
    'transfer',
    'in_transfer',
    'out_transfer',
    'waiting_accept',
    'transfer_initiated',
    'transfer_waiting_confirmation',
  ],
  stolen: ['stolen'],
  'cancel request': ['cancel_request'],
  lost: ['lost'],
}

export const DISPLAYED_DATA_DEFAULTS = {
  product_name: true,
  certificate_id: true,
  menu_item: true,
  /*
   * OFF by default (VRG-720).
   *
   * The other three are what the card IS — a photo, a name, an identifier, and
   * the control to act on it. Activity is a SECOND fact about the certificate,
   * and it is the only one of the four that costs a whole band of card height
   * and a divider to say. Defaulting it on spends that on every card in the
   * list for a line most people are not reading on most visits, and it is the
   * one row whose presence varies card to card, so it also makes the grid
   * ragged by default (see `CertificatesTeaser`'s note on the stretch).
   *
   * It stays one click away in the Display popover, and the toggle now animates
   * (see the card's `Collapse`), which is what makes an off-by-default
   * discoverable rather than hidden: turning it on is visibly a reveal.
   */
  last_activity: false,
}

/**
 * The default list/grid layout, and the value the Display popover's Reset
 * restores.
 *
 * Hoisted here beside `DISPLAYED_DATA_DEFAULTS` because it now has TWO readers —
 * `useCertificatesPageState`'s initializer (the localStorage fallback) and
 * `DisplayedDataPopover`'s Reset. A Reset that restores its own copy of the
 * default is a Reset that stops agreeing with the default the moment one of them
 * changes, which is the drift working-conventions §36 names.
 *
 * GRID rather than list (VRG-720).
 *
 * It used to be list, on a measured argument that has since expired: grid fitted
 * 2 cards above the fold against list's 3. That was the 2-up `xs` grid, whose
 * card was a 163.5px shell — the photo was a tile on a card. The grid is one card
 * per row at `xs` now and the photo is 341px, the full width of the card, so the
 * count above the fold is no longer the question the choice turns on. What is
 * above the fold is one PRODUCT, at a size you can recognise it by, which is what
 * a list of things you own is for.
 */
export const DEFAULT_LAYOUT: 'list' | 'grid' = 'grid'

export const SORT_OPTIONS = [
  { key: 'name', label: 'Certificate name' },
  { key: 'status', label: 'Status' },
  { key: 'last_activity', label: 'Last activity date' },
] as const

export type SortOption = typeof SORT_OPTIONS[number]
